import React, { useState } from 'react';
import { Box, Typography } from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';

// Sent-log at the bottom of the notifications page. A single date selector runs
// across the top; below it the sends are grouped by day (newest first) as a
// tight, flat activity list — time, the message, and who it reached — matching
// the admin's Intercom-style surfaces. "All" shows the whole week.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const atMidnight = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const startOfWeek = () => {
  const d = atMidnight(new Date());
  d.setDate(d.getDate() - d.getDay());
  return d;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const monthDayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

// Parse a timestamp defensively: a row with a missing/unparseable `sentAt`
// returns null so it can be dropped rather than crashing the whole log.
const parseDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
    }}
  >
    {children}
  </Typography>
);

// Compact date pill, styled like the admin's filter pills rather than a raw MUI
// chip — small, quiet when inactive, solid when selected.
const DatePill = ({ label, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      border: (theme) => (active ? '1px solid transparent' : `1px solid ${hairline(theme)}`),
      borderRadius: '8px',
      px: '10px',
      height: '26px',
      fontFamily: 'inherit',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'background-color 0.12s ease, color 0.12s ease',
      color: (theme) => (active ? theme.palette.primary.contrastText : theme.palette.text.secondary),
      backgroundColor: (theme) => (active ? theme.palette.primary.main : 'transparent'),
      '&:hover': { backgroundColor: (theme) => (active ? theme.palette.primary.main : softFill(theme)) },
    }}
  >
    {label}
  </Box>
);

// One sent notification: time, the copy (single line, full text on hover), and
// a muted reach line. Flat rows separated by hairlines — no card chrome.
const SentRow = ({ broadcast, cityName }) => (
  <Box
    sx={{
      display: 'flex',
      gap: '12px',
      py: '8px',
      borderTop: (theme) => `1px solid ${hairline(theme)}`,
      opacity: broadcast._local ? 0.6 : 1,
    }}
  >
    <Typography
      sx={{ fontSize: '12px', color: 'text.secondary', flex: '0 0 62px', fontVariantNumeric: 'tabular-nums', pt: '1px' }}
    >
      {parseDate(broadcast.sentAt) ? timeFmt.format(parseDate(broadcast.sentAt)) : ''}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        title={broadcast.body}
        sx={{
          fontSize: '13px',
          color: 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {broadcast.body}
      </Typography>
      <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mt: '2px' }}>
        {[
          cityName,
          broadcast.origin && broadcast.destination ? `${broadcast.origin} → ${broadcast.destination}` : null,
          `${broadcast.recipientCount} ${broadcast.recipientCount === 1 ? 'driver' : 'drivers'}`,
          // Mark which segment the send targeted: XL-only sends read "XL only";
          // the default non-XL sends show nothing to keep the log quiet.
          broadcast.xlOnly === true ? 'XL only' : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
    </Box>
  </Box>
);

const SentLog = ({ items = [], cityNameById = {}, loading = false, error = null }) => {
  const today = atMidnight(new Date());
  const weekStart = startOfWeek();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  // End of the displayed week (exclusive), so "All" stays scoped to this
  // calendar week and never leaks the tail of last week that the rolling
  // history fetch may include — keeping the list in step with the day pills.
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);

  const [selectedDay, setSelectedDay] = useState(null); // null = All (whole week)

  const dayShort = (day) => (sameDay(day, today) ? 'Today' : `${WEEKDAYS[day.getDay()]} ${day.getDate()}`);

  const visible = items
    .map((b) => ({ b, sentDate: parseDate(b.sentAt) }))
    .filter(({ sentDate }) => sentDate !== null)
    .filter(({ sentDate }) =>
      selectedDay ? sameDay(sentDate, selectedDay) : sentDate >= weekStart && sentDate < weekEnd,
    )
    .sort((x, y) => y.sentDate - x.sentDate)
    .map(({ b }) => b);

  // Group the visible sends by calendar day, preserving newest-first order.
  const groups = [];
  visible.forEach((b) => {
    const d = atMidnight(parseDate(b.sentAt));
    const key = d.getTime();
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, day: d, sends: [] };
      groups.push(group);
    }
    group.sends.push(b);
  });

  const groupLabel = (day) =>
    `${sameDay(day, today) ? 'Today' : WEEKDAYS[day.getDay()]} · ${monthDayFmt.format(day)}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', mb: '12px' }}>
        <SectionLabel>Sent this week</SectionLabel>
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <DatePill label="All" active={selectedDay === null} onClick={() => setSelectedDay(null)} />
          {days.map((day) => (
            <DatePill
              key={day.toISOString()}
              label={dayShort(day)}
              active={selectedDay !== null && sameDay(day, selectedDay)}
              onClick={() => setSelectedDay(day)}
            />
          ))}
        </Box>
      </Box>

      {error ? (
        // On a failed load, show only the error — never leave stale cached rows
        // on screen where they could read as current data.
        <Typography sx={{ fontSize: '13px', color: 'error.main', py: '8px' }}>
          Couldn’t load the sent log: {error}
        </Typography>
      ) : visible.length === 0 ? (
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: '8px' }}>
          {loading && items.length === 0
            ? 'Loading…'
            : selectedDay
            ? `Nothing sent on ${dayShort(selectedDay)}.`
            : 'Nothing sent this week.'}
        </Typography>
      ) : (
        groups.map((group) => (
          <Box key={group.key} sx={{ mb: '14px' }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                color: (theme) => (sameDay(group.day, today) ? theme.palette.primary.main : theme.palette.text.secondary),
                mb: '2px',
              }}
            >
              {groupLabel(group.day)}
            </Typography>
            {group.sends.map((b) => (
              <SentRow key={b.id} broadcast={b} cityName={cityNameById[b.homeCity] || b.homeCity} />
            ))}
          </Box>
        ))
      )}
    </Box>
  );
};

export default SentLog;
